/**
 * The shared base of the client and worker themes (UX 1.0d, RM24-UI-alus §1–§2).
 *
 * Every colour the two portals use lives HERE, once, as a token, and
 * `foundation.test.ts` re-checks every pairing against WCAG the moment one
 * changes.
 *
 * Brand (decision F1, Marti): the orange #F5833F stays as the FILL of the
 * prominent components, always with dark text on it (`brandFill` +
 * `onBrandFill`, 6.93:1). It never carries white text (2.56:1) and is never
 * text itself (2.56:1 on paper). `palette.primary.main` stays the dark,
 * text-safe orange #B84D0E, because MUI uses `primary.main` as TEXT (links,
 * text / outlined buttons, checkboxes, the focus ring): a fill component the
 * overrides below forget still lands on a compliant colour, never on a
 * failing one. The client and the worker portal share this one brand.
 *
 * The old client palette failed in five places: white on #F5833F (≈ 2.6:1),
 * the selected menu item (≈ 3.0:1), white on error #EF4444 (≈ 3.8:1),
 * success/warning as text on white (≈ 2.3 / 2.2:1) and `text.secondary` on the
 * page background (≈ 4.4:1).
 *
 * The admin theme (`theme.ts`) does NOT use this file: admin pages are out of
 * scope for the client/worker UX work and stay exactly as they were.
 */

import type { Components, Theme, ThemeOptions } from '@mui/material/styles';

import { mergeComponents } from './mobileOverrides';

// ─── Colour tokens ──────────────────────────────────────────────────────────

export const color = {
  /**
   * `palette.primary.main`: text, links, focus ring, selected/checked states,
   * and the message bubble with white text. Text-safe on every light surface.
   */
  brand: '#B84D0E',
  /** White text on `brand` / `brandText` (message bubble, env badge). */
  onBrand: '#FFFFFF',
  /** Brand-coloured text; the selected menu / bottom-bar item on `brandSoft`. */
  brandText: '#9A3F0B',
  /** Soft brand surface (selected menu / bottom-bar item). */
  brandSoft: '#FFF1E7',

  /**
   * The brand orange as a FILL (F1): contained primary button, primary Fab,
   * filled primary Chip, primary Badge, shell avatars, browser theme-color.
   * Only `onBrandFill` text on it — never white, and never as text itself.
   */
  brandFill: '#F5833F',
  /** Text and icons on `brandFill` and its hover / active states. */
  onBrandFill: '#111827',
  /** `brandFill` under the pointer. */
  brandFillHover: '#E37A3D',
  /** `brandFill` while pressed. */
  brandFillActive: '#DA763C',

  error: '#B91C1C',
  success: '#15803D',
  warning: '#B45309',
  info: '#1D4ED8',
  /** Text on any of the four status fills. */
  onStatus: '#FFFFFF',

  textPrimary: '#111827',
  textSecondary: '#4B5563',
  secondary: '#1E293B',

  /** Page background. */
  bgDefault: '#F4F5F7',
  /** Cards, header, drawer, dialogs. */
  bgPaper: '#FFFFFF',
  divider: '#E5E7EB',
  /**
   * Card / drawer / header hairline. Decorative only (the card is also set
   * apart by its paper fill), so no 3:1 requirement and not in contrastPairs.
   */
  cardBorder: '#EEF0F3',
} as const;

export type ColorToken = keyof typeof color;

/**
 * Every foreground/background pairing the themes produce, WITH the background
 * it is valid on. A token is not "accessible" on its own — `brandText` passes
 * on `brandSoft` (6.15) and `success` passes on the page background only just
 * (4.60) — so the pair is the unit that is documented and tested.
 *
 * `kind: 'text'` needs ≥ 4.5:1 (WCAG 1.4.3), `kind: 'ui'` ≥ 3:1 (1.4.11:
 * focus ring, borders that carry meaning). `ratio` is the documented WCAG
 * ratio, rounded to two decimals; the test recomputes it, so the table here
 * cannot drift from the tokens.
 */
export interface ContrastPair {
  name: string;
  fg: ColorToken;
  bg: ColorToken;
  kind: 'text' | 'ui';
  ratio: number;
}

export const contrastPairs: readonly ContrastPair[] = [
  // Brand fill (F1) — dark text on the orange
  { name: 'contained primary button / Fab / filled chip / badge / avatar', fg: 'onBrandFill', bg: 'brandFill', kind: 'text', ratio: 6.93 },
  { name: 'contained primary button, hover', fg: 'onBrandFill', bg: 'brandFillHover', kind: 'text', ratio: 6.01 },
  { name: 'contained primary button, pressed', fg: 'onBrandFill', bg: 'brandFillActive', kind: 'text', ratio: 5.61 },
  // Brand as text (palette.primary.main) — links, text/outlined buttons, checks
  { name: 'primary text / link on paper', fg: 'brand', bg: 'bgPaper', kind: 'text', ratio: 5.11 },
  { name: 'primary text / link on page', fg: 'brand', bg: 'bgDefault', kind: 'text', ratio: 4.69 },
  { name: 'primary text on selected item', fg: 'brand', bg: 'brandSoft', kind: 'text', ratio: 4.62 },
  { name: 'selected menu / bottom-bar item', fg: 'brandText', bg: 'brandSoft', kind: 'text', ratio: 6.15 },
  { name: 'outlined primary button, hover', fg: 'brandText', bg: 'brandSoft', kind: 'text', ratio: 6.15 },
  { name: 'brand text on paper', fg: 'brandText', bg: 'bgPaper', kind: 'text', ratio: 6.8 },
  { name: 'brand text on page', fg: 'brandText', bg: 'bgDefault', kind: 'text', ratio: 6.23 },
  // White on the dark orange (message bubble = primary.main; env badge)
  { name: 'own message bubble (white on primary.main)', fg: 'onBrand', bg: 'brand', kind: 'text', ratio: 5.11 },
  { name: 'env badge / own attachment chip (white on brandText)', fg: 'onBrand', bg: 'brandText', kind: 'text', ratio: 6.8 },
  // Focus ring (2 px, offset 2 — it sits on the surrounding surface, M9)
  { name: 'focus ring on paper', fg: 'brand', bg: 'bgPaper', kind: 'ui', ratio: 5.11 },
  { name: 'focus ring on page', fg: 'brand', bg: 'bgDefault', kind: 'ui', ratio: 4.69 },
  { name: 'focus ring on selected item', fg: 'brand', bg: 'brandSoft', kind: 'ui', ratio: 4.62 },
  // Status colours as fills (filled chips, contained buttons, badges)
  { name: 'white on error fill', fg: 'onStatus', bg: 'error', kind: 'text', ratio: 6.47 },
  { name: 'white on success fill', fg: 'onStatus', bg: 'success', kind: 'text', ratio: 5.02 },
  { name: 'white on warning fill', fg: 'onStatus', bg: 'warning', kind: 'text', ratio: 5.02 },
  { name: 'white on info fill', fg: 'onStatus', bg: 'info', kind: 'text', ratio: 6.7 },
  // Status colours as text
  { name: 'error text on paper', fg: 'error', bg: 'bgPaper', kind: 'text', ratio: 6.47 },
  { name: 'error text on page', fg: 'error', bg: 'bgDefault', kind: 'text', ratio: 5.93 },
  { name: 'success text on paper', fg: 'success', bg: 'bgPaper', kind: 'text', ratio: 5.02 },
  { name: 'success text on page', fg: 'success', bg: 'bgDefault', kind: 'text', ratio: 4.6 },
  { name: 'warning text on paper', fg: 'warning', bg: 'bgPaper', kind: 'text', ratio: 5.02 },
  { name: 'warning text on page', fg: 'warning', bg: 'bgDefault', kind: 'text', ratio: 4.6 },
  { name: 'info text on paper', fg: 'info', bg: 'bgPaper', kind: 'text', ratio: 6.7 },
  { name: 'info text on page', fg: 'info', bg: 'bgDefault', kind: 'text', ratio: 6.14 },
  // Neutrals
  { name: 'body text on paper', fg: 'textPrimary', bg: 'bgPaper', kind: 'text', ratio: 17.74 },
  { name: 'body text on page', fg: 'textPrimary', bg: 'bgDefault', kind: 'text', ratio: 16.26 },
  { name: 'secondary text on paper', fg: 'textSecondary', bg: 'bgPaper', kind: 'text', ratio: 7.56 },
  { name: 'secondary text on page', fg: 'textSecondary', bg: 'bgDefault', kind: 'text', ratio: 6.93 },
  { name: 'secondary text on selected item', fg: 'textSecondary', bg: 'brandSoft', kind: 'text', ratio: 6.83 },
];

/**
 * Pairs that must NEVER be produced (F1). The orange fill fails both as a
 * background for white and as a colour for text; the test keeps them failing
 * so nobody "fixes" a token into them.
 */
export const forbiddenPairs: readonly { name: string; fg: string; bg: string; ratio: number }[] = [
  { name: 'white text on the orange fill', fg: color.onBrand, bg: color.brandFill, ratio: 2.56 },
  { name: 'the orange fill as text on paper', fg: color.brandFill, bg: color.bgPaper, ratio: 2.56 },
  { name: 'the orange fill as text on page', fg: color.brandFill, bg: color.bgDefault, ratio: 2.35 },
];

// ─── WCAG arithmetic (used by the test; pure) ──────────────────────────────

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG 2.x relative luminance of a `#RRGGBB` colour. */
export function relativeLuminance(hex: string): number {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) throw new Error(`Not a #RRGGBB colour: ${hex}`);
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => channel(parseInt(h, 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio, 1–21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// ─── Palette ────────────────────────────────────────────────────────────────

export interface PortalAccent {
  main: string;
  dark: string;
  contrastText: string;
}

/**
 * The one accent of the client AND the worker portal (F1). `main` is the
 * text-safe dark orange; `contrastText` (white) is what MUI puts on `main`
 * where nothing below overrides it — e.g. the own message bubble (5.11).
 */
export const brandAccent: PortalAccent = {
  main: color.brand,
  dark: color.brandText,
  contrastText: color.onBrand,
};

export function foundationPalette(accent: PortalAccent): ThemeOptions['palette'] {
  return {
    mode: 'light',
    primary: { ...accent },
    secondary: { main: color.secondary },
    // `contrastText` explicit: MUI would otherwise compute it, and a filled
    // status chip is exactly where the old theme fell under 4.5:1.
    error: { main: color.error, contrastText: color.onStatus },
    success: { main: color.success, contrastText: color.onStatus },
    warning: { main: color.warning, contrastText: color.onStatus },
    info: { main: color.info, contrastText: color.onStatus },
    background: { default: color.bgDefault, paper: color.bgPaper },
    text: { primary: color.textPrimary, secondary: color.textSecondary },
    divider: color.divider,
  };
}

// ─── Typography ─────────────────────────────────────────────────────────────

/**
 * Body text and inputs are a FIXED 16 px: iOS zooms the page into any input
 * under 16 px, and the old fluid `clamp()` went down to 15. `caption` (12 px)
 * is for metadata only — never a state or a consequence.
 */
export const foundationTypography = {
  fontFamily:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  body1: { fontSize: '1rem' },
  body2: { fontSize: '0.875rem' },
  caption: { fontSize: '0.75rem' },
  // Sentence case, never uppercase (RM24-UI-alus §1 Kiri).
  button: { textTransform: 'none' as const, fontWeight: 600 },
};

// ─── Component rules: focus, scroll padding, motion, touch targets ─────────

/**
 * The focus ring's offset, px. Fable M9: the ring (#B84D0E) against the
 * orange fill is only ≈ 2.0:1, so it must never sit ON a filled control — the
 * 2 px offset puts it on the surrounding paper / page, where it is 5.11 /
 * 4.69. Pinned by the test; do not set it to 0.
 */
export const FOCUS_RING_OFFSET = 2;

/** Smallest touch target on a coarse pointer (M3 48 dp; WCAG 2.5.8). */
export const TOUCH_TARGET = 48;

const COARSE = '@media (pointer: coarse)';
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

/** The slots that get the 48 px floor — and only these (touch contract). */
export const TOUCH_SLOTS = [
  '.MuiButton-root',
  '.MuiIconButton-root',
  '.MuiListItemButton-root',
  '.MuiMenuItem-root',
  '.MuiTab-root',
  '.MuiBottomNavigationAction-root',
] as const;

/**
 * Where a button sits inside something whose height must not change — a table
 * row, an input's adornment (Autocomplete's clear/open buttons, a password
 * eye). There the target grows with an invisible pseudo-element instead of
 * `min-height`, so the row / field keeps its height.
 */
const INLINE_CONTEXTS = ['.MuiTableCell-root', '.MuiInputBase-root'] as const;

/** An invisible, centred hit area of at least 48 × 48 around the element. */
const hitAreaPseudo = {
  content: '""',
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  width: '100%',
  height: '100%',
  minWidth: TOUCH_TARGET,
  minHeight: TOUCH_TARGET,
  transform: 'translate(-50%, -50%)',
};

const where = (selectors: readonly string[]) => `:where(${selectors.join(', ')})`;

/**
 * The 48 px floor as ZERO-specificity global rules (`:where()`), not as
 * component styleOverrides.
 *
 * In styleOverrides the `@media (pointer: coarse)` block lands in the same
 * class as the element's `sx`, after it, at equal specificity — so it beat
 * every explicit size: the worker's 56 px primary buttons ("Proovi uuesti",
 * WorkerJobDetailPage) and the 64 px bottom-bar actions all measured 48 on a
 * phone. At specificity 0 any class rule wins over the floor: an explicit
 * `minHeight` (larger OR smaller, e.g. a 44 px table action) is respected,
 * and the floor only fills in where nothing set a height.
 */
export const touchFloorRules = {
  [COARSE]: {
    [where(TOUCH_SLOTS)]: { minHeight: TOUCH_TARGET },
    [where(['.MuiIconButton-root'])]: { minWidth: TOUCH_TARGET },
    // Inline contexts: no floor (same specificity, later rule), hit area instead.
    [`${where(INLINE_CONTEXTS)} ${where(TOUCH_SLOTS)}`]: { minHeight: 0 },
    [`${where(INLINE_CONTEXTS)} ${where(['.MuiIconButton-root'])}`]: { minWidth: 0 },
    [`${where(INLINE_CONTEXTS)} ${where(TOUCH_SLOTS)}::after`]: hitAreaPseudo,
  },
};

/**
 * The hidden native input IS the hit area of Checkbox / Radio / Switch (MUI
 * lays it over the control with opacity 0). Growing it to 48 px, centred,
 * makes the target bigger without moving a single pixel of layout — a
 * pseudo-element on the wrapper would not work, because a click on it never
 * reaches the input.
 */
const enlargeSwitchInput = (selector: string) => ({
  [COARSE]: {
    [`& ${selector}`]: {
      top: '50%',
      left: '50%',
      width: `max(100%, ${TOUCH_TARGET}px)`,
      height: `max(100%, ${TOUCH_TARGET}px)`,
      transform: 'translate(-50%, -50%)',
    },
  },
});

export function foundationComponents(focusColor: string): Components<Theme> {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          // The sticky header (64/72 px + notch) and the worker bottom bar
          // must never cover the element that just received focus or a
          // `#hash` target (WCAG 2.4.11). `--worker-navigation-offset` is set
          // by WorkerShell; elsewhere it is unset and falls back to 0.
          scrollPaddingTop: 'calc(80px + env(safe-area-inset-top, 0px))',
          scrollPaddingBottom: 'calc(var(--worker-navigation-offset, 0px) + 16px)',
        },
        // One visible focus ring for everything (WCAG 2.4.7 / 2.4.13): 2 px,
        // ≥ 3:1 against paper and page (see contrastPairs), offset so it sits
        // on the background, not on the control's own fill. `html` in the
        // selector outranks MUI's `outline: 0` on ButtonBase.
        'html :focus-visible': {
          outline: `2px solid ${focusColor}`,
          outlineOffset: FOCUS_RING_OFFSET,
        },
        // A text field already shows focus with its 2 px primary border; a
        // second ring on the inner <input> would only add noise.
        'html .MuiInputBase-input:focus-visible': {
          outline: 'none',
        },
        ...touchFloorRules,
        [REDUCED_MOTION]: {
          '*, *::before, *::after': {
            transitionDuration: '0ms !important',
            transitionDelay: '0ms !important',
            scrollBehavior: 'auto !important',
          },
          '.MuiTouchRipple-root': { display: 'none' },
          '.MuiSkeleton-root, .MuiSkeleton-root::after': { animation: 'none !important' },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        // MUI renders subtitle1/subtitle2 as <h6>, so every bold card label,
        // address and section caption became a level-6 heading and broke the
        // outline (axe heading-order on every client/worker view). They are
        // paragraphs; a subtitle that really titles a section says so with
        // an explicit `component="h2"` / `"h3"`. Other variants: MUI defaults
        // (Typography falls back to its own map for keys not listed here).
        variantMapping: { subtitle1: 'p', subtitle2: 'p' },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        // Explicit as well as via body1: a `size="small"` field or a page-level
        // `fontSize` must not take an input under 16 px (iOS zoom).
        root: { fontSize: '1rem' },
      },
    },

    // ─── Touch-target contract (pointer: coarse) ───
    // The 48 px floor for TOUCH_SLOTS is in CssBaseline (touchFloorRules).
    // One exception: MUI itself lowers MenuItem to `auto` from 600 px up, and
    // that class rule would beat the zero-specificity floor on a touch
    // tablet, so MenuItem re-states 48 there (no menu item sets its own
    // height in the client/worker portals).
    MuiMenuItem: {
      styleOverrides: {
        root: {
          [`${COARSE} and (min-width: 600px)`]: {
            minHeight: TOUCH_TARGET,
            [`${INLINE_CONTEXTS.map((c) => `${c} &`).join(', ')}`]: { minHeight: 0 },
          },
        },
      },
    },

    // Small controls: hit area without changing their size.
    MuiCheckbox: { styleOverrides: { root: enlargeSwitchInput('.PrivateSwitchBase-input') } },
    MuiRadio: { styleOverrides: { root: enlargeSwitchInput('.PrivateSwitchBase-input') } },
    MuiSwitch: {
      styleOverrides: {
        // The Switch input is already 300 % wide over the track; only its
        // height needs to reach 48.
        input: {
          [COARSE]: {
            top: '50%',
            height: `max(100%, ${TOUCH_TARGET}px)`,
            transform: 'translateY(-50%)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        clickable: {
          [COARSE]: {
            position: 'relative',
            '&::after': hitAreaPseudo,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        // 24 px line + 2 × 12 px padding = 48. Padding, not min-height.
        option: {
          [COARSE]: { paddingTop: 12, paddingBottom: 12 },
        },
      },
    },
  };
}

/**
 * F1: the components that FILL with the primary colour get the orange fill
 * with dark text, explicitly — `primary.main` itself stays the dark,
 * text-safe orange. Hover / pressed use their own keys (`@media (hover:
 * hover)`, `&:active`) so a portal's `'&:hover'` (clientTheme's shadow reset)
 * merges beside them instead of replacing them.
 *
 * Disabled controls keep MUI's grey: its `.Mui-disabled` rule is more
 * specific than these.
 */
export function brandFillComponents(): Components<Theme> {
  const fill = { backgroundColor: color.brandFill, color: color.onBrandFill };
  return {
    MuiButton: {
      styleOverrides: {
        // MUI v6 paints the contained button from these variables.
        containedPrimary: {
          '--variant-containedBg': color.brandFill,
          '--variant-containedColor': color.onBrandFill,
          '@media (hover: hover)': {
            '&:hover': { '--variant-containedBg': color.brandFillHover },
          },
          '&:active': { '--variant-containedBg': color.brandFillActive },
        },
        // MUI tints the hover with 4 % of primary.main, which takes
        // primary.main text on the page background under 4.5:1. Hover is
        // the soft brand surface with brand text instead (6.15).
        outlinedPrimary: {
          '@media (hover: hover)': {
            '&:hover': { '--variant-outlinedBg': color.brandSoft, '--variant-outlinedColor': color.brandText },
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        primary: {
          ...fill,
          '@media (hover: hover)': {
            '&:hover': { backgroundColor: color.brandFillHover },
          },
          '&:active': { backgroundColor: color.brandFillActive },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        filledPrimary: {
          ...fill,
          '& .MuiChip-deleteIcon': {
            color: color.onBrandFill,
            '&:hover, &:active': { color: color.onBrandFill },
          },
          '&.MuiChip-clickable:hover, &.Mui-focusVisible': {
            backgroundColor: color.brandFillHover,
          },
          '&.MuiChip-clickable:active': { backgroundColor: color.brandFillActive },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        colorPrimary: fill,
      },
    },
  };
}

/**
 * The full component set for a portal theme: foundation rules, the brand
 * fill (F1), then the portal's own overrides on top. The caller wraps the
 * result in `withMobileComponents` (which stays the single place for phone
 * layout).
 */
export function extendFoundation(
  focusColor: string,
  own: Components<Theme> = {},
): Components<Theme> {
  return mergeComponents(mergeComponents(foundationComponents(focusColor), brandFillComponents()), own);
}
